import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createProject, updateProject, getProjectById } from '../services/projectService';
import { Form, Button, Alert, Spinner, Row, Col, Card } from 'react-bootstrap';
import { fetchAllTeams } from '../services/action';

const ProjectForm = ({ isEdit }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState('');
  const [availableTeams, setAvailableTeams] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'active',
    startDate: '',
    endDate: '',
    teams: [], // New field for selected teams
    location: {
      address: '',
      city: '',
      country: ''
    }
  });

  useEffect(() => {
    // Fetch available teams
    const fetchTeams = async () => {
      try {
        const response = await fetchAllTeams();
        setAvailableTeams(response.data.data);
      } catch (err) {
        console.error('Error fetching teams:', err);
      }
    };
    
    fetchTeams();
  }, []);

   useEffect(() => {
    if (isEdit) {
      const fetchProject = async () => {
        try {
          const res = await getProjectById(id);
          setFormData({
            ...res.data.data,
            teams: res.data.data.teams.map(t => t._id) // Assuming teams are populated with _id
          });
          setLoading(false);
        } catch (err) {
          setError(err.response?.data?.message || 'Error loading project');
          setLoading(false);
        }
      };
      fetchProject();
    }
  }, [id, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (isEdit) {
        await updateProject(id, formData);
      } else {
        await createProject(formData);
      }
      navigate('/projects');
    } catch (err) {
      setError(err.response?.data?.message || 'Operation failed');
    }
  };

 const handleChange = (e) => {
    if (e.target.name === 'teams') {
      const selectedOptions = Array.from(e.target.selectedOptions)
        .map(option => option.value);
      setFormData({ ...formData, teams: selectedOptions });
    }
    else if (e.target.name.startsWith('location.')) {

      const locationField = e.target.name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        location: {
          ...prev.location,
          [locationField]: e.target.value
        }
      }));
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
  };

  if (loading) {
    return <div className="text-center mt-5"><Spinner animation="border" /></div>;
  }

  return (
    <div className="container py-4">
      <h2 className="mb-4 text-center">{isEdit ? 'Edit Project' : 'Create New Project'}</h2>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="shadow-sm p-4">
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select name="status" value={formData.status} onChange={handleChange}>
                  <option value="active">Active</option>
                  <option value="on-hold">On Hold</option>
                  <option value="completed">Completed</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
            />
          </Form.Group>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  min={formData.startDate}
                />
              </Form.Group>
            </Col>
          </Row>

          <Card className="p-3 mt-3">
            <Card.Title>Location Details</Card.Title>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type="text"
                    name="location.address"
                    value={formData.location.address}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="location.city"
                    value={formData.location.city}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Country</Form.Label>
                  <Form.Control
                    type="text"
                    name="location.country"
                    value={formData.location.country}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card>
          <Card className="p-3 mt-3">
            <Card.Title>Team Assignment</Card.Title>
            <Form.Group className="mb-3">
              <Form.Label>Select Teams</Form.Label>
              <Form.Control
                as="select"
                multiple
                name="teams"
                value={formData.teams}
                onChange={handleChange}
                style={{ minHeight: '120px' }}
              >
                {availableTeams.map(team => (
                  <option key={team._id} value={team._id}>
                    {team.name} - {team.members.length} members
                  </option>
                ))}
              </Form.Control>
              <Form.Text className="text-muted">
                Hold Ctrl/Cmd to select multiple teams
              </Form.Text>
            </Form.Group>
          </Card>

          <div className="text-end mt-4">
            <Button variant="primary" type="submit">
              {isEdit ? 'Update Project' : 'Create Project'}
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default ProjectForm;